import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { ADFEntity } from '@atlaskit/adf-utils/types';
import type { EditorCommand } from '@atlaskit/editor-common/types';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { conditionalHooksFactory } from '@atlaskit/platform-feature-flags-react/conditional-hooks-factory/conditional-hooks-factory';
import { updatePageRovoParams } from '@atlaskit/rovo-triggers';
import { type RovoChatOpenMode } from '@atlaskit/rovo-triggers/params-types';

import {
	CONFLUENCE_AIFC_WHITEBOARD_CREATE_KEY,
	CONFLUENCE_AIFC_WHITEBOARD_UPDATE_KEY,
	isRemixGeneratedFlow,
} from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import { getRemixFeatureArea } from '@atlassian/conversation-assistant-instrumentation/controllers/experience-tracker/remix-feature-area';
import { useStagingAreaState } from '@atlassian/conversation-assistant-store/controllers/staging-area';
import type { StoreChatItem } from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { useCurrentConversationId } from '@atlassian/conversation-assistant/controllers/chat-store';
import { findLastAssistantAction } from '@atlassian/conversation-assistant/controllers/chat-store/utils';
import { RemixContentComponent } from '@atlassian/malleable-ui-components/editor-remix-creator';
import {
	REMIX_EXPANDS_EXPERIMENT,
	isAllowedMBEExtensionForRemix,
	isRemixRestrictedNodeTypeWithExpands,
} from '@atlassian/malleable-ui-components/remix/remix-config';
import {
	endRemix,
	getRemixMachine,
	sendRemixEvent,
	startRemix,
} from '@atlassian/malleable-ui-components/state/machine';
import type {
	EditorRemixLifecycleAdapter,
	EditorRemixLifecycleHandle,
	EditorRemixMachineLifecycleSession,
	EditorRemixLifecycleStartOutcome,
	EditorRemixLifecycleStartRequest,
	HandoffAcknowledgementResult,
	RemixContentComponentProps,
	RemixFinalizeSuccessPayload,
	RemixInsertSuccessPayload,
	RemixProductConfig,
	RemixProductIntegration,
} from '@atlassian/malleable-ui-components/types';
import type { SmartCreationContext } from '@atlassian/conversation-assistant/ui/staging-area-modal';

import { ConfluenceConversationAssistant } from '@confluence/conversation-assistant/entry-points/main.ts';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { type WithFlagsProps, withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import {
	useSmartCreationModalActions,
	useSmartCreationModalState,
} from '@confluence/smart-creation-modal-store/entry-points/smartCreationModalStore.tsx';
import { useToggleConversationAssistant } from '@confluence/conversation-assistant/entry-points/useToggleConversationAssistant.ts';
import { usePageContentId, useContentType, usePageSpaceKey } from '@confluence/page-context';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { expVal, expValEquals, isExperimentEnabled } from '@confluence/feature-experiments';
import {
	Message,
	publishMessage,
} from '@confluence/page-tree-refresh-state-container/entry-points/messageBus.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useConversationAssistantConfig } from '@confluence/smart-create/entry-points/useConfig.ts';
import { FeedbackVariant } from '@confluence/smart-create/entry-points/FeedbackVariant.ts';
import { useLazyShowFeedbackFlag } from '@confluence/smart-create/entry-points/useLazyShowFeedbackFlag.ts';
import { useSpaceDetail } from '@confluence/space-utils/entry-points/useSpaceDetail.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';

import { EditorPageRemixModal as ObjectRemixComponent } from './PageRemixContainer.tsx';
import { preloadRemixWatcherHost } from './RemixWatcherMount.tsx';
import { useEditorRemixParityDiagnostics } from './useEditorRemixParityDiagnostics.ts';
import { useShouldHideDiagramRemixOption } from './useShouldHideDiagramRemixOption.ts';

/**
 * Bounds the wait for the Rovo sidebar to show the requested conversation. Distinct from the
 * insertion acknowledgement timeout: expiry means "no proof of handoff", not an insertion outcome.
 */
const ROVO_CHAT_HANDOFF_ACKNOWLEDGEMENT_TIMEOUT_MS = 10_000;

const REMIX_MBE_ALLOW_LIST: ReadonlyArray<string> = ['native-tabs'];
const REMIX_NO_EMPTY_CHECK_EXTENSION_ALLOW_LIST: ReadonlySet<string> = new Set([
	'native-embed:page',
]);

/** Only invoked for expand node types, so other selections never read the experiment. */
const areRemixExpandsEnabled = (): boolean => isExperimentEnabled(REMIX_EXPANDS_EXPERIMENT);

export const isNodeUnsupportedForConfluenceRemix = (node: ADFEntity): boolean => {
	if (node.type === 'multiBodiedExtension') {
		return !isAllowedMBEExtensionForRemix(node.attrs?.extensionKey, REMIX_MBE_ALLOW_LIST);
	}

	if (
		node.type === 'extension' &&
		typeof node.attrs?.extensionKey === 'string' &&
		REMIX_NO_EMPTY_CHECK_EXTENSION_ALLOW_LIST.has(node.attrs.extensionKey) &&
		fg('maui_remixable_page_native_embeds')
	) {
		return false;
	}

	return isRemixRestrictedNodeTypeWithExpands(node.type, areRemixExpandsEnabled);
};

const confluenceProductConfig: RemixProductConfig = {
	productKey: 'confluence',
	analyticsProductName: 'confluence',
	getRemixGeneratePreviewMetadata: ({ type }) =>
		type === 'infographic'
			? {
					isConfluenceInfographicsTwoStageEnabled: fg('confluence-infographics-two-stage'),
				}
			: {},
	isNodeUnsupportedForRemix: isNodeUnsupportedForConfluenceRemix,
	shouldUseEphemeralPreview: (chatMessages: StoreChatItem[]): boolean => {
		for (let i = chatMessages.length - 1; i >= 0; i--) {
			const message = chatMessages[i];
			if (message.type === 'ACTION_PREPARE') {
				return message.action ? isRemixGeneratedFlow(message.action) : false;
			}
		}
		return false;
	},
	isActionReady: (chatMessages: StoreChatItem[]): boolean => {
		const hasWhiteboard =
			findLastAssistantAction(
				chatMessages,
				(action) =>
					action.key === CONFLUENCE_AIFC_WHITEBOARD_CREATE_KEY ||
					action.key === CONFLUENCE_AIFC_WHITEBOARD_UPDATE_KEY,
			) !== undefined;
		if (hasWhiteboard) {
			return true;
		}
		for (let i = chatMessages.length - 1; i >= 0; i--) {
			const message = chatMessages[i];
			if (message.type === 'ACTION_COMPLETE') {
				return true;
			}
			if (message.type === 'HUMAN') {
				return false;
			}
		}
		return false;
	},
};

type EditorRemixLifecycleOperation =
	| 'endRemix'
	| 'sendCancelled'
	| 'sendTransition'
	| 'startRemix'
	| 'validateAdmissionIdentity';

const useEditorRemixLifecycleAdapter = (
	onRegisterEditorRemixMachineLifecycleOwnership: RemixContentComponentProps['onRegisterEditorRemixMachineLifecycleOwnership'],
): EditorRemixLifecycleAdapter => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { releaseFlow, recordLegacyPhase } = useEditorRemixParityDiagnostics();
	const logLifecycleOperationError = useCallback(
		(operation: EditorRemixLifecycleOperation, error: unknown): void => {
			try {
				createAnalyticsEvent({
					type: 'sendOperationalEvent',
					data: {
						action: 'failed',
						actionSubject: 'editorRemixLifecycleOperation',
						source: 'editorRemix',
						attributes: {
							operation,
							errorName: error instanceof Error ? error.name : typeof error,
						},
					},
				}).fire();
			} catch {
				// Diagnostics must never affect lifecycle behavior.
			}
		},
		[createAnalyticsEvent],
	);

	const createHandle = useCallback(
		({ conversationId, remixId }: { conversationId: string; remixId: string }) => {
			let hasSettled = false;

			const end: EditorRemixLifecycleHandle['end'] = (reason) => {
				if (hasSettled) {
					return;
				}
				hasSettled = true;
				// Stop observing before the machine goes away: a released flow has no phase to compare
				// against, and settlement is not evidence of divergence.
				releaseFlow(remixId);
				try {
					endRemix(remixId, reason);
				} catch (error) {
					logLifecycleOperationError('endRemix', error);
				}
			};

			const transition: EditorRemixLifecycleHandle['transition'] = (event) => {
				if (hasSettled) {
					return false;
				}
				try {
					const phaseBefore = getRemixMachine(remixId)?.state();
					sendRemixEvent(remixId, event);
					const accepted = phaseBefore !== getRemixMachine(remixId)?.state();
					if (accepted && event.type === 'INSERTED') {
						hasSettled = true;
						releaseFlow(remixId);
					}
					return accepted;
				} catch (error) {
					logLifecycleOperationError('sendTransition', error);
					return false;
				}
			};

			const rollback: EditorRemixLifecycleHandle['rollback'] = () => {
				if (hasSettled) {
					return;
				}

				// Mandatory cleanup is structurally independent of the CANCELLED publish and its logging.
				try {
					sendRemixEvent(remixId, { type: 'CANCELLED' });
				} catch (error) {
					logLifecycleOperationError('sendCancelled', error);
				} finally {
					end('cancelled');
				}
			};

			return {
				conversationId,
				remixId,
				rollback,
				transition,
				end,
				onRetained: (session: EditorRemixMachineLifecycleSession) => {
					if (!onRegisterEditorRemixMachineLifecycleOwnership) {
						throw new Error('Editor Remix lifecycle ownership registration is unavailable');
					}
					return onRegisterEditorRemixMachineLifecycleOwnership(session);
				},
			};
		},
		[logLifecycleOperationError, onRegisterEditorRemixMachineLifecycleOwnership, releaseFlow],
	);

	const startFlow = useCallback(
		(request: EditorRemixLifecycleStartRequest): EditorRemixLifecycleStartOutcome => {
			if (!fg('remix_state_rearchitecture_enabled')) {
				return { status: 'disabled' };
			}

			// Normal evaluation is intentional here: the master gate prevents early exposure, and a
			// startFlow attempt is the editor experiment's enrollment boundary.
			if (!isExperimentEnabled('remix_state_rearchitecture_editor')) {
				return { status: 'disabled' };
			}

			// The background-phase watcher host may not have loaded yet on a fresh page. Start its
			// fetch now so the admitted flow is observed without waiting on the chunk.
			preloadRemixWatcherHost();

			try {
				// Neutral metadata is deliberate for this dormant foundation. Producer wiring will define
				// the content attribution carried by the lifecycle machine.
				const result = startRemix({ ...request, metadata: {} });
				if (!result) {
					return { status: 'active-flow-rejected' };
				}

				const handle = createHandle(result);
				if (
					request.conversationId !== undefined &&
					result.conversationId !== request.conversationId
				) {
					logLifecycleOperationError(
						'validateAdmissionIdentity',
						new Error('Machine admission returned a different conversation identity'),
					);
					handle.rollback();
					return { status: 'shadow-infrastructure-failure' };
				}

				// An admitted flow's ProseMirror lifecycle is machine-derived, so there is no second
				// phase to compare: parity observation is not started for it.
				return {
					status: 'admitted',
					handle,
				};
			} catch (error) {
				logLifecycleOperationError('startRemix', error);
				return { status: 'shadow-infrastructure-failure' };
			}
		},
		[createHandle, logLifecycleOperationError],
	);

	return useMemo(
		() => ({ startFlow, observeLegacyPhase: recordLegacyPhase }),
		[recordLegacyPhase, startFlow],
	);
};

const useConfluenceProductIntegration = ({
	dismissSubtypeAdjustedFlag,
	flags,
	onFinalizeFailed,
	rootPageTreeParentId,
}: {
	dismissSubtypeAdjustedFlag?: () => void;
	flags: FlagsStateContainer;
	onFinalizeFailed?: RemixProductIntegration['onFinalizeFailed'];
	rootPageTreeParentId?: string;
}): RemixProductIntegration => {
	const { showSmartCreationModal } = useSmartCreationModalState();
	const { openModal, closeModal } = useSmartCreationModalActions();
	const {
		isConversationAssistantSidebarActive,
		openConversationAssistant: openAssistant,
		toggleConversationAssistant: toggleAssistant,
	} = useToggleConversationAssistant();
	const experienceTracker = useAIMateExperienceTracker();
	const { dynamicUiType, dynamicUiSubtype } = useStagingAreaState();
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { showModal } = useDialogs();
	const [spaceKey] = usePageSpaceKey();
	const [contentId] = usePageContentId();
	const [contentType] = useContentType();
	const isLivePage = useIsCurrentPageLive();
	const pageMode = useGetPageMode();
	const { cloudId } = useSessionData();
	const showCreateWithRovoModalCloseFeedbackFlag = useLazyShowFeedbackFlag(
		flags,
		intl,
		showModal,
		createAnalyticsEvent,
	);
	// Global chat store (`ChatStoreContainer isGlobal`): the conversation the sidebar is showing.
	const currentConversationId = useCurrentConversationId();
	const pendingConversationOpenRef = useRef<
		| {
				conversationId: string;
				resolve: (result: HandoffAcknowledgementResult) => void;
				timer: ReturnType<typeof setTimeout>;
		  }
		| undefined
	>(undefined);

	const settlePendingConversationOpen = useCallback((result: HandoffAcknowledgementResult) => {
		const pending = pendingConversationOpenRef.current;
		if (!pending) {
			return;
		}
		pendingConversationOpenRef.current = undefined;
		clearTimeout(pending.timer);
		pending.resolve(result);
	}, []);

	const isRequestedConversationOpen = useCallback(
		(requestedConversationId: string) =>
			isConversationAssistantSidebarActive && requestedConversationId === currentConversationId,
		[currentConversationId, isConversationAssistantSidebarActive],
	);

	useEffect(() => {
		const pending = pendingConversationOpenRef.current;
		if (pending && isRequestedConversationOpen(pending.conversationId)) {
			settlePendingConversationOpen({ outcome: 'acknowledged' });
		}
	}, [isRequestedConversationOpen, settlePendingConversationOpen]);

	useEffect(
		() => () => settlePendingConversationOpen({ outcome: 'abandoned', reason: 'unmounted' }),
		[settlePendingConversationOpen],
	);

	const requestConversationAssistantOpen = useCallback(
		(location: string, conversationId?: string): Promise<HandoffAcknowledgementResult> =>
			new Promise((resolve) => {
				settlePendingConversationOpen({ outcome: 'abandoned', reason: 'superseded' });
				if (conversationId) {
					updatePageRovoParams({ pathway: 'chat', conversationId });
				}
				openAssistant(location as RovoChatOpenMode);
				if (!conversationId) {
					// Positive acknowledgement needs a conversation to observe; opening alone proves nothing.
					resolve({ outcome: 'rejected' });
					return;
				}
				if (isRequestedConversationOpen(conversationId)) {
					// Already showing the requested conversation; no store change will follow.
					resolve({ outcome: 'acknowledged' });
					return;
				}
				// Sidebar visibility alone is not acceptance; wait for the store, bounded.
				const timer = setTimeout(() => {
					if (pendingConversationOpenRef.current?.resolve === resolve) {
						settlePendingConversationOpen({ outcome: 'timed-out' });
					}
				}, ROVO_CHAT_HANDOFF_ACKNOWLEDGEMENT_TIMEOUT_MS);
				pendingConversationOpenRef.current = { conversationId, resolve, timer };
			}),
		[isRequestedConversationOpen, openAssistant, settlePendingConversationOpen],
	);

	const onRemixInsertSuccess = useCallback(
		({ currentConversationId, remixMetadata, requestId }: RemixInsertSuccessPayload) => {
			if (expValEquals('cc_maui_polish_changes_batch_4', 'isEnabled', true)) {
				dismissSubtypeAdjustedFlag?.();
			}

			void showCreateWithRovoModalCloseFeedbackFlag({
				feedbackVariant: FeedbackVariant.AFTER_REMIX_INSERT,
				contentType,
				contentMode: contentType === 'page' ? pageMode : undefined,
				cloudId,
				currentConversationId,
				remixMetadata,
				requestId,
			});
		},
		[
			cloudId,
			contentType,
			dismissSubtypeAdjustedFlag,
			pageMode,
			showCreateWithRovoModalCloseFeedbackFlag,
		],
	);

	const publishPageTreeRefresh = useCallback(
		(parentId: string | undefined) => {
			const pageTreeParentId = parentId ?? rootPageTreeParentId;
			if (!pageTreeParentId) {
				return;
			}

			publishMessage(Message.PageTreeRefreshRequested, {
				parentId: pageTreeParentId,
			});
		},
		[rootPageTreeParentId],
	);

	const onFinalizeSuccess = useCallback(
		({ parentId }: RemixFinalizeSuccessPayload) => {
			publishPageTreeRefresh(parentId);
		},
		[publishPageTreeRefresh],
	);

	return useMemo<RemixProductIntegration>(
		() => ({
			closeConversationAssistant: () => {
				if (isConversationAssistantSidebarActive) {
					toggleAssistant('sidebar');
				}
			},
			contentType: contentType ?? 'page',
			sourceContent: contentId ? { contentId, contentType } : undefined,
			isRemixPreviewVisible: showSmartCreationModal,
			onRemixInsertSuccess,
			onFinalizeSuccess,
			onFinalizeFailed,
			requestConversationAssistantOpen,
			openRemixPreview: ({
				isFloating,
				remixConversationId,
				onOpen,
				onSubmit,
				editorInsertionAcknowledgement,
				onDismiss,
			}) => {
				const remixAifcHandoffAttributes = {
					featureArea:
						getRemixFeatureArea({
							remixType: dynamicUiType,
							existingFeatureArea: 'remix',
						}) ?? 'remix',
					entryPoint: 'editor' as const,
					...(remixConversationId && { conversationChannelId: remixConversationId }),
					...(dynamicUiType && { remixType: dynamicUiType }),
					...(dynamicUiSubtype && { remixSubtype: dynamicUiSubtype }),
				};
				experienceTracker.start({
					name: ExperienceName.REMIX_AIFC_HANDOFF,
					id: ExperienceName.REMIX_AIFC_HANDOFF,
					attributes: remixAifcHandoffAttributes,
				});
				openModal({
					entryPoint: 'dynamicUi',
					spaceKey: spaceKey ?? undefined,
					stagingAreaActive: true,
					isFloating,
					shouldShowBackButton: false,
					onOpenCallback: () => {
						experienceTracker.succeed({
							name: ExperienceName.REMIX_AIFC_HANDOFF,
							attributes: remixAifcHandoffAttributes,
						});
						onOpen?.();
					},
					...(onSubmit ? { onSubmitCallback: onSubmit } : {}),
					// Both floating and full-modal previews report insertion through the modal
					// callback, so the retained lifecycle settles from the insert path itself.
					...(editorInsertionAcknowledgement ? { editorInsertionAcknowledgement } : {}),
					...(onDismiss
						? {
								onCloseCallback: () => {
									// Smart Creation invokes this callback immediately before its synchronous
									// close action. Defer lifecycle settlement until that legacy UI close lands.
									// The observer must never surface into the close path, so swallow a throw
									// instead of leaving an unhandled rejection.
									void Promise.resolve()
										.then(onDismiss)
										.catch(() => {});
								},
							}
						: {}),
					...(remixConversationId && expValEquals('cc-maui-experiment', 'isEnabled', true)
						? { remixConversationId }
						: {}),
					content: contentId
						? {
								id: contentId,
								type: contentType ?? 'page',
								subType: isLivePage ? 'live' : null,
							}
						: undefined,
					// Pass submitCallback only for ephemeral (floating) previews so the full
					// modal flow (isFloating: false) still falls through to
					// handleSubmitSmartCreate and redirects to the newly created content.
					// Mirrors useRemixToAIFC.
					...(isFloating
						? {
								submitCallback: async () => {
									if (
										contentId &&
										expVal('cc-maui-refresh-page-tree-with-diagram', 'isEnabled', false)
									) {
										publishPageTreeRefresh(contentId);
									}
								},
							}
						: {}),
				});
			},
			closeRemixPreview: closeModal,
			openConversationAssistant: (location: string, conversationId?: string) => {
				if (conversationId) {
					updatePageRovoParams({
						pathway: 'chat',
						conversationId,
					});
				}
				openAssistant(location as RovoChatOpenMode);
			},
			toggleConversationAssistant: (location: string) => {
				toggleAssistant(location as RovoChatOpenMode);
			},
		}),
		[
			isConversationAssistantSidebarActive,
			showSmartCreationModal,
			openModal,
			closeModal,
			openAssistant,
			toggleAssistant,
			spaceKey,
			contentId,
			contentType,
			isLivePage,
			onRemixInsertSuccess,
			onFinalizeSuccess,
			onFinalizeFailed,
			requestConversationAssistantOpen,
			publishPageTreeRefresh,
			dynamicUiType,
			dynamicUiSubtype,
			experienceTracker,
		],
	);
};

const useConfig = () => {
	const { cloudId } = useSessionData();

	return useConversationAssistantConfig({
		assistanceServiceConfigHeaders: {
			'X-Cloudid': cloudId,
		},
		preventAbortRequestsOnStagingModalClose: true,
	});
};

// remove this when cleanup platform_editor_ai_remix_in_editor_loading
function useConfigOld() {
	return undefined;
}

const useConfigSafe = conditionalHooksFactory(
	() => fg('platform_editor_ai_remix_in_editor_loading'),
	useConfig,
	useConfigOld,
);

const useSmartCreationContext = (): SmartCreationContext => {
	const [spaceKey] = usePageSpaceKey();
	const [contentId] = usePageContentId();
	const [contentType] = useContentType();
	const isLivePage = useIsCurrentPageLive();
	const { data: spaceDetail } = useSpaceDetail(spaceKey, {
		includeSpaceTypeSettings: true,
	});

	const context: SmartCreationContext = {
		confluence: {
			space: spaceDetail
				? {
						id: spaceDetail.spaceId,
						name: spaceDetail.name,
						// `key` is needed to construct correct whiteboard URLs
						// (`/wiki/spaces/{spaceKey}/whiteboard/...`). Without it,
						// `useOnComplete` falls back to `~{numericSpaceId}`, which
						// the page-level URL routing treats as a personal-space key
						// and resolves to the wrong space.
						key: spaceKey ?? undefined,
						iconPath: spaceDetail.iconPath,
						typeSettings: spaceDetail.typeSettings,
					}
				: undefined,
			parentContent: spaceDetail
				? {
						id: spaceDetail.homepage?.id ?? '',
						title: spaceDetail.homepage?.title ?? '',
						contentType: spaceDetail.homepage?.type ?? '',
					}
				: undefined,
			content: contentId
				? {
						id: contentId,
						type: contentType ?? 'page',
						subType: isLivePage ? 'live' : null,
					}
				: undefined,
		},
	};

	return context;
};

// remove this when cleanup platform_editor_ai_remix_in_editor_loading
function useSmartCreationContextOld() {
	return undefined;
}

const useSmartCreationContextSafe = conditionalHooksFactory(
	() => fg('platform_editor_ai_remix_in_editor_loading'),
	useSmartCreationContext,
	useSmartCreationContextOld,
);

const PlatformComponentInner = ({
	flags,
	...props
}: RemixContentComponentProps & WithFlagsProps) => {
	const smartCreationConfig = useConfigSafe();
	const smartCreationContext = useSmartCreationContextSafe();
	const dismissSubtypeAdjustedFlag = useCallback(() => {
		const editorApi = props.editorApi as
			| (RemixContentComponentProps['editorApi'] & {
					malleableUi?: {
						commands: {
							dismissSubtypeAdjustedFlag?: () => EditorCommand;
						};
					};
			  })
			| undefined;
		const command = editorApi?.malleableUi?.commands.dismissSubtypeAdjustedFlag?.();

		if (command) {
			editorApi?.core.actions.execute(command);
		}
	}, [props.editorApi]);
	const productIntegration = useConfluenceProductIntegration({
		dismissSubtypeAdjustedFlag,
		flags,
		onFinalizeFailed: props.onFinalizeFailed,
		rootPageTreeParentId: smartCreationContext?.confluence?.parentContent?.id,
	});
	const editorRemixLifecycleAdapter = useEditorRemixLifecycleAdapter(
		props.onRegisterEditorRemixMachineLifecycleOwnership,
	);
	const hiddenOptions = useShouldHideDiagramRemixOption();

	return (
		<RemixContentComponent
			{...props}
			editorRemixLifecycleAdapter={editorRemixLifecycleAdapter}
			entryPoint={fg('platform_editor_ai_remix_in_editor_loading') ? 'dynamicUi' : undefined}
			smartCreationConfig={
				fg('platform_editor_ai_remix_in_editor_loading') ? smartCreationConfig : undefined
			}
			smartCreationContext={
				fg('platform_editor_ai_remix_in_editor_loading') ? smartCreationContext : undefined
			}
			hiddenOptions={
				hiddenOptions.length > 0
					? [...(props.hiddenOptions ?? []), ...hiddenOptions]
					: props.hiddenOptions
			}
			productConfig={confluenceProductConfig}
			productIntegration={productIntegration}
		/>
	);
};

const PlatformComponent = withFlags(PlatformComponentInner);

export const createRemixContentComponentPlatform = (): ((
	props: RemixContentComponentProps,
) => React.JSX.Element | null) => {
	const Wrapper = (props: RemixContentComponentProps) => {
		const { onStopThinking, isThinking, isModalOpen } = props;
		const { showSmartCreationModal } = useSmartCreationModalState();
		const isSmartCreationModalOpen = useRef(false);

		const isObjectRemix = props.remixType === 'object';

		useLayoutEffect(() => {
			if (fg('platform_editor_ai_remix_in_editor_loading')) {
				return;
			}
			if (
				isSmartCreationModalOpen.current &&
				!showSmartCreationModal &&
				isThinking &&
				(!isObjectRemix || !fg('maui_remixable_page_native_embeds'))
			) {
				onStopThinking();
			}
			isSmartCreationModalOpen.current = showSmartCreationModal;
		}, [showSmartCreationModal, isThinking, onStopThinking, isObjectRemix]);

		let remixContent;
		if (isObjectRemix) {
			const { remixType, adfDocument, ...restProps } = props;

			remixContent = fg('maui_remixable_page_native_embeds') ? (
				<ObjectRemixComponent {...restProps} remixType={remixType} adfDocument={adfDocument} />
			) : (
				<PlatformComponent {...restProps} remixType="block" />
			);
		} else {
			remixContent = <PlatformComponent {...props} />;
		}

		if (fg('platform_editor_ai_remix_in_editor_loading')) {
			return <ConfluenceConversationAssistant>{remixContent}</ConfluenceConversationAssistant>;
		}

		if (!(isModalOpen || isThinking)) {
			return null;
		}

		return <ConfluenceConversationAssistant>{remixContent}</ConfluenceConversationAssistant>;
	};

	return Wrapper;
};
